// Alta de la empresa a partir del cuestionario de onboarding
// (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
//
// Antes la empresa se creaba en `/onboarding`, un form que pedía nombre, país, marca y
// rubro. Ese form ya no existe: el cuestionario nuevo NO pregunta nada de eso, así que
// los cuatro datos salen de otro lado:
//
//   nombre → del registro (metadata del signUp, o el perfil de Google)
//   marca  → la del paso 1 del cuestionario corto, con `"Mi marca"` de respaldo
//            (ADR-2026-10-31-onboarding-corto-para-la-prueba)
//   país   → deducido: el backend lo saca de la IP del request; acá se manda la
//            heurística por timezone como respaldo (`paisDelBrowser`)
//   rubro  → el del paso 2, traducido de id a label
//
// Se dispara al terminar el **paso del rubro**, antes del de producto: ése necesita
// `id_company` y créditos para generar de verdad, y así quien abandona a mitad del
// cuestionario no deja una empresa vacía.

import { supabase } from './supabase';
import { createCompanyForUser } from './createCompany';
import { ensureDefaultBrand } from './ensureDefaultBrand';
import { cuestionarioCompletado } from './cuestionarioObligatorio';
import { paisDelBrowser } from './paisDelBrowser';
import { nombreDelUsuario } from './nombreUsuario';
import { isPaidPlan } from './planCapabilities';
import {
  SETUP_BRAND_NAME_KEY,
  SETUP_BRAND_NAME_MAX,
  SETUP_CATEGORIES_KEY,
  rubroDesdeCategorias,
} from '../components/onboarding/accountSetup';

/** Nombre por defecto de la marca. El usuario lo cambia después, desde la app. */
export const MARCA_POR_DEFECTO = 'Mi marca';

export interface AltaResult {
  ok: boolean;
  companyId?: string | null;
  /** Mensaje listo para mostrar. Sólo viene cuando `ok` es false. */
  error?: string;
}

function leerArray(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as string[]).filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** El nombre de la marca del paso 1, o `"Mi marca"` si no hay (o no se pudo leer). */
export function nombreDeMarcaDelCuestionario(): string {
  try {
    const n = (localStorage.getItem(SETUP_BRAND_NAME_KEY) ?? '').trim().slice(0, SETUP_BRAND_NAME_MAX);
    return n || MARCA_POR_DEFECTO;
  } catch {
    return MARCA_POR_DEFECTO;
  }
}

/**
 * Crea la empresa con lo que juntó el cuestionario corto (nombre y rubro). Idempotente del
 * lado del backend: un doble submit no crea una segunda empresa. El rol y los tipos de
 * contenido ya no se preguntan acá: los pregunta el onboarding de marca, al pagar.
 */
export async function crearEmpresaDesdeCuestionario(): Promise<AltaResult> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return { ok: false, error: 'Se cerró la sesión. Volvé a iniciar sesión.' };

  const categorias = leerArray(SETUP_CATEGORIES_KEY);
  const nombreDeLaMarca = nombreDeMarcaDelCuestionario();

  const res = await createCompanyForUser({
    userId: user.id,
    name: nombreDelUsuario(user),
    // El backend pisa esto con el país deducido de la IP; esto es el respaldo para
    // cuando el servicio de geo no responde. `paisDelBrowser` devuelve null si no
    // puede afirmar nada, y ahí "Otro" es la respuesta honesta.
    pais: paisDelBrowser() ?? 'Otro',
    companyName: nombreDeLaMarca,
    industry: rubroDesdeCategorias(categorias),
  });
  if (!res.ok) return res;

  // El rubro, escrito de forma AUTORITATIVA.
  //
  // `createCompanyForUser` ya lo intenta en su paso 3, pero ahí es best-effort: vive en
  // un `try/catch` mudo y se saltea entero si no pudo resolver el `id_company`. Eso
  // alcanzaba cuando la empresa se creaba recién en este momento —el backend la inserta
  // CON el rubro, así que el update del front era un refuerzo—, pero dejó de alcanzar
  // con el alta por link invertida: ahí la empresa ya existe (se creó al registrarse,
  // con el centinela, para poder cobrar en un paso), el backend es idempotente y NO la
  // pisa, y este update es lo ÚNICO que saca el centinela.
  //
  // Y si el centinela no se va, la app lo devuelve al cuestionario una y otra vez. Por
  // eso acá el fallo se REPORTA en vez de tragarse: el usuario reintenta y sale.
  // Ver ADR-2026-09-22-el-alta-por-link-cobra-primero-y-pregunta-despues.
  let esDeLaPrueba = false;
  if (res.companyId) {
    const rubroElegido = rubroDesdeCategorias(categorias);
    // En el mismo update, sólo si la empresa NO tiene un plan pago: el nombre (el backend no
    // lo pisa si la empresa ya existía, por ejemplo la de quien había arrancado el onboarding
    // largo con "Mi marca") y que debe el onboarding de marca cuando empiece a pagar
    // (ADR-2026-10-31-onboarding-corto-para-la-prueba). El alta es idempotente: un cliente pago
    // que vuelve a esta pantalla por una pestaña vieja recibe SU empresa, y no puede quedar
    // obligado ni con el nombre pisado. Si el plan no se pudo leer, tampoco se la marca: ante
    // la duda, nadie queda obligado.
    const { data: filaPlan, error: errorPlan } = await supabase
      .from('company_profiles')
      .select('plan')
      .eq('id_company', res.companyId)
      .maybeSingle();
    esDeLaPrueba = !errorPlan && filaPlan != null && !isPaidPlan((filaPlan as { plan?: string | null }).plan);
    const { error: errorRubro } = await supabase
      .from('company_profiles')
      .update(
        esDeLaPrueba
          ? { rubro: rubroElegido, nombre: nombreDeLaMarca, debe_onboarding_marca: true }
          : { rubro: rubroElegido },
      )
      .eq('id_company', res.companyId);
    if (errorRubro) {
      return { ok: false, error: 'No pudimos guardar tu rubro. Probá de nuevo.' };
    }
    // El rubro real ya está escrito: el guardia de navegación deja de mandar acá. Va
    // ACÁ y no al final porque lo que sigue (marca, tipos de contenido) puede pedir un
    // reintento, y ese reintento no tiene que ser rebotado al paso 1.
    cuestionarioCompletado();
    // Y el de la MARCA, que es una copia aparte: `ensureDefaultBrand` la crea a partir
    // del rubro de la EMPRESA, así que en el alta por link nació con el centinela. Ese
    // helper ahora la repara si le pasás el rubro real, y se lo llama de nuevo acá —y
    // no sólo desde `createCompanyForUser`— porque allá es best-effort y se saltea
    // entero si no pudo resolver el id. Los modos se gatean por `brand_profiles.rubro`
    // sin caer al de la empresa: sin esto, el que pagó no ve los de su rubro.
    // Si la reparación de la marca falla, el rubro de la EMPRESA ya quedó escrito, así
    // que el rebote de `App` no vuelve a traer a esta persona por acá: éste es el único
    // momento en que se puede pedir el reintento. Sin eso, la marca se queda en blanco y
    // los gates de modos —que leen `brand_profiles.rubro` con igualdad exacta, sin caer
    // al de la empresa— le niegan Plantillas, Diseño, Moodboards, Catálogo de muebles e
    // Infografías a alguien que acaba de pagar.
    if (!(await ensureDefaultBrand(res.companyId, rubroElegido))) {
      return {
        ok: false,
        error: 'No pudimos terminar de configurar tu marca. Probá de nuevo.',
      };
    }
  } else if (res.ok) {
    // El alta dio success pero no sabemos el `id_company`: las dos lecturas de
    // `idCompanyDe` fallaron (`createCompanyForUser` ya lo reporta a Sentry). Sin ese
    // id no se escribió el rubro, ni la marca default, ni los tipos de contenido.
    //
    // Se REPORTA en vez de devolver success, que es lo que hacía antes: en el alta por
    // link la empresa ya existía con el centinela y el backend es idempotente, así que
    // este update es lo ÚNICO que lo saca — con un success acá el cuestionario terminaba
    // "bien", el centinela quedaba, y el rebote de `App` devolvía a la misma persona al
    // cuestionario en cada carga. Justo el loop que el comentario de arriba dice evitar.
    //
    // En el camino normal el rubro sí quedó bien (lo escribe el INSERT del backend), pero
    // igual falta la marca default: el reintento es idempotente y sale barato.
    return {
      ok: false,
      error: 'No pudimos terminar de configurar tu cuenta. Probá de nuevo.',
    };
  }

  // La marca default se llama como la marca: `ensureDefaultBrand` la crea con el nombre de la
  // empresa, pero si ya existía (con "Mi marca") no la toca. Best-effort a propósito: la empresa
  // ya existe y el usuario tiene que poder seguir; el nombre se cambia después desde la app.
  if (res.companyId && esDeLaPrueba && nombreDeLaMarca !== MARCA_POR_DEFECTO) {
    try {
      await supabase
        .from('brand_profiles')
        .update({ name: nombreDeLaMarca })
        .eq('id_company', res.companyId)
        .eq('is_default', true)
        .eq('name', MARCA_POR_DEFECTO);
    } catch {
      /* best-effort */
    }
  }

  return res;
}
